import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import emendaService, { resolveEmendaStatus } from '../services/emendaService';
import ConfirmActionModal from './ConfirmActionModal';
import ActionButton from './ui/ActionButton';
import PanelHeader from './ui/PanelHeader';
import StatusBadge from './ui/StatusBadge';
import Card from './ui/Card';
import { FIELD_CLASS } from './ui/fieldStyles';
import { toUpperText } from '../utils/formatters';

interface Props {
  emendaId: string;
  emendaStatus?: string;
  canAct?: boolean;
  onStatusChange?: (newStatus: string) => void;
}

// Painel exibido quando uma emenda foi reprovada na demanda ou na análise
// documental e retornou ao setor de Orçamento. O Orçamento é quem formaliza a
// devolução à Câmara (ação DEVOLVER_CAMARA), disponibilizando-a na Área de
// Trabalho da Câmara para ciência do parlamentar.
const OrcamentoDevolucaoPanel: React.FC<Props> = ({ emendaId, emendaStatus, canAct = false, onStatusChange }) => {
  const queryClient = useQueryClient();
  const [parecer, setParecer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isRetornoDemanda = emendaStatus === 'Retornada ao Orçamento (demanda)';
  const isRetornoDocumental = emendaStatus === 'Retornada ao Orçamento (documental)';

  const acaoMutation = useMutation({
    mutationFn: (observacao?: string) =>
      emendaService.executarAcao(emendaId, { acao: 'DEVOLVER_CAMARA', observacao }),
    onSuccess: (updated) => {
      setError(null); setParecer('');
      queryClient.invalidateQueries({ queryKey: ['historico', emendaId] });
      if (onStatusChange) onStatusChange(resolveEmendaStatus(updated));
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.response?.data?.error ?? 'Erro ao executar a ação'),
  });

  // Todos os hooks acima SEMPRE são chamados; só depois decidimos não renderizar.
  // (Um return antecipado antes de um hook viola as Rules of Hooks — React #300.)
  if (!isRetornoDemanda && !isRetornoDocumental) return null;

  const origem = isRetornoDemanda ? 'incompatibilidade de demanda' : 'inviabilidade documental';

  return (
    <Card>
      <PanelHeader title="Devolução à Câmara" owner="Orçamento"
        right={<StatusBadge tone="warning">Aguardando devolução</StatusBadge>} />

      <div className="space-y-3 px-4 py-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-900/30">
          <p className="text-xs leading-5 text-amber-800 dark:text-amber-200">
            Emenda retornada ao Orçamento por {origem}. Confira a justificativa no histórico e
            formalize a devolução à Câmara para disponibilizá-la na Área de Trabalho do parlamentar.
          </p>
        </div>

        {!canAct && (
          <p className="text-xs text-slate-500 dark:text-slate-400">Somente o setor de Orçamento pode devolver a emenda à Câmara.</p>
        )}

        {canAct && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-100/70 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <div>
              <label htmlFor="parecer-devolucao-camara" className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Observação da devolução <span className="font-normal text-fg-subtle">(opcional)</span>
              </label>
              <textarea
                id="parecer-devolucao-camara"
                value={parecer}
                onChange={(e) => setParecer(toUpperText(e.target.value))}
                rows={3}
                placeholder="Complemente a devolução à Câmara, se necessário…"
                className={`${FIELD_CLASS} resize-none`}
              />
            </div>
            <div className="flex justify-center pt-1">
              <ActionButton kind="reject" size="md"
                onClick={() => { setError(null); setConfirmOpen(true); }}
                disabled={acaoMutation.isPending} loading={acaoMutation.isPending}>
                Devolver à Câmara
              </ActionButton>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>

      <ConfirmActionModal
        open={confirmOpen}
        title="Confirmar devolução à Câmara"
        message="Confirma a devolução desta emenda à Câmara? Ela ficará disponível na Área de Trabalho do parlamentar para ciência."
        confirmLabel="Devolver"
        cancelLabel="Cancelar"
        onConfirm={() => { acaoMutation.mutate(parecer.trim() || undefined); setConfirmOpen(false); }}
        onCancel={() => setConfirmOpen(false)}
        loading={acaoMutation.isPending}
      />
    </Card>
  );
};

export default OrcamentoDevolucaoPanel;
